import { useState, useEffect } from 'react';
import { Shield, Users, X, Check, RefreshCw, Activity, Map, Plus, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '../App';

const ROADMAP_STATUSES = ['STABLE', 'IN_PROGRESS', 'PLANNED', 'BACKLOG'];
const STATUS_COLORS = {
  STABLE: '#00ff88',
  IN_PROGRESS: '#00f0ff',
  PLANNED: '#ffd700',
  BACKLOG: '#888'
};

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastReset, setLastReset] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [demoStats, setDemoStats] = useState(null);
  const [wipingDemo, setWipingDemo] = useState(false);
  const [repairingFuture, setRepairingFuture] = useState(false);
  const [repairingMulti, setRepairingMulti] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [orgInvite, setOrgInvite] = useState({ name: '', tag: '', owner_handle: '', owner_email: '' });
  const [orgInviteBusy, setOrgInviteBusy] = useState(false);
  const [orgInviteMsg, setOrgInviteMsg] = useState(null);
  
  // Roadmap state
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({ title: '', description: '', status: 'PLANNED' });

  useEffect(() => {
    if (user?.is_admin) fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, resetRes, roadmapRes, demoRes, orgsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/last-reset`).catch(() => ({ data: { last_reset: null } })),
        axios.get(`${API}/admin/roadmap`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/demo/stats`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/orgs`).catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data || []);
      setLastReset(resetRes.data?.last_reset);
      setRoadmapItems(roadmapRes.data || []);
      setDemoStats(demoRes.data);
      setOrgs(orgsRes.data || []);
    } catch (error) {
      console.error('Admin fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetReports = async () => {
    if (!window.confirm('Reset ALL reports? This cannot be undone.')) return;
    setResetting(true);
    try {
      const res = await axios.post(`${API}/admin/reset-reports`);
      setLastReset(res.data.reset_at);
      alert('Reports reset successfully');
    } catch (e) {
      alert('Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const handleAssignBadge = async (userId, badgeId) => {
    if (!badgeId) return;
    try {
      const action = badgeId === 'REMOVE_SUPPORTER' ? 'remove' : 'add';
      await axios.post(`${API}/admin/users/${userId}/badge`, { action, badge_id: badgeId });
      fetchData();
    } catch (e) {
      alert('Badge assignment failed');
    }
  };

  const handleDeleteUser = async (u) => {
    const ok = window.prompt(
      `Type the user's RSI handle "${u.rsi_handle}" to permanently delete this account and all of their contracts/bids. This cannot be undone.`
    );
    if (ok !== u.rsi_handle) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleWipeDemo = async () => {
    const ok = window.confirm(
      'Wipe ALL demo accounts, demo contracts, and demo bids?\n\nThis is irreversible. The next demo login will reseed fresh sample content.'
    );
    if (!ok) return;
    setWipingDemo(true);
    try {
      const r = await axios.post(`${API}/admin/demo/wipe`);
      alert(`Wiped: ${r.data.users_deleted} users, ${r.data.contracts_deleted} contracts, ${r.data.bids_deleted} bids.`);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Wipe failed');
    } finally {
      setWipingDemo(false);
    }
  };

  const handleRepairFuture = async () => {
    const ok = window.confirm(
      'Repair Future Time posts whose expiry was incorrectly anchored to creation time?\n\nOnly contracts where timing_type = "future" and expires_at < scheduled_start_at will be touched. Cancelled and completed contracts are left alone.'
    );
    if (!ok) return;
    setRepairingFuture(true);
    try {
      const r = await axios.post(`${API}/admin/contracts/repair-future-expiry`);
      alert(
        `Repair complete.\n\n` +
        `Scanned: ${r.data.candidates_scanned}\n` +
        `Repaired: ${r.data.repaired}\n` +
        `Restored to OPEN: ${r.data.restored_to_open}\n` +
        `Skipped: ${r.data.skipped}\n` +
        `Window: ${r.data.window_hours}h after scheduled_start_at`
      );
    } catch (e) {
      alert(e.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairingFuture(false);
    }
  };

  const handleRepairMultiJoin = async () => {
    const ok = window.confirm(
      'Repair Future Time / LFG posts that were locked into single-slot mode after one accepted user?\n\nOnly future or LFG posts with status in_progress/active and a single accepted_by will be reopened to multi-join. Completed and cancelled posts are left alone.'
    );
    if (!ok) return;
    setRepairingMulti(true);
    try {
      const r = await axios.post(`${API}/admin/contracts/repair-multi-join-events`);
      alert(
        `Multi-join repair complete.\n\n` +
        `Scanned: ${r.data.candidates_scanned}\n` +
        `Repaired: ${r.data.repaired}\n` +
        `Default max participants: ${r.data.default_max_participants}`
      );
    } catch (e) {
      alert(e.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairingMulti(false);
    }
  };

  const handleInviteOrgOwner = async () => {
    setOrgInviteBusy(true);
    setOrgInviteMsg(null);
    try {
      const payload = {
        name: orgInvite.name.trim(),
        tag: orgInvite.tag.trim().toUpperCase(),
      };
      if (orgInvite.owner_email.trim()) payload.owner_email = orgInvite.owner_email.trim().toLowerCase();
      else if (orgInvite.owner_handle.trim()) payload.owner_handle = orgInvite.owner_handle.trim();
      const r = await axios.post(`${API}/orgs`, payload);
      setOrgInviteMsg({ type: 'ok', text: `Created [${r.data.tag}] ${r.data.name} (pending approval).` });
      setOrgInvite({ name: '', tag: '', owner_handle: '', owner_email: '' });
      fetchData();
    } catch (e) {
      setOrgInviteMsg({ type: 'err', text: e.response?.data?.detail || 'Failed to invite owner' });
    } finally {
      setOrgInviteBusy(false);
      setTimeout(() => setOrgInviteMsg(null), 5000);
    }
  };


  const handleApproveOrg = async (id, approve) => {
    try {
      await axios.post(`${API}/admin/orgs/${id}/${approve ? 'approve' : 'reject'}`);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Action failed');
    }
  };

  const handleReassignOrg = async (o) => {
    const input = window.prompt(
      `Reassign [${o.tag}] ${o.name} to a new owner.\n\nEnter RSI handle or email:`,
      ''
    );
    if (!input) return;
    const val = input.trim();
    const payload = val.includes('@') ? { owner_email: val.toLowerCase() } : { owner_handle: val };
    try {
      await axios.post(`${API}/admin/orgs/${o.id}/reassign`, payload);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Reassign failed');
    }
  };

  const handleDeleteOrg = async (o) => {
    if (!window.confirm(`Permanently delete [${o.tag}] ${o.name}?\n\nThis cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/orgs/${o.id}`);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleExportUsers = (optInOnly = false) => {
    const list = optInOnly ? users.filter(u => u.contact_opt_in) : users;
    const rows = [['RSI Handle', 'Email', 'Verified', 'Contact Opt-In', 'Supporter Tier', 'Joined']];
    list.forEach(u => {
      rows.push([
        u.rsi_handle || '',
        u.email || '',
        u.is_verified ? 'Yes' : 'No',
        u.contact_opt_in ? 'Yes' : 'No',
        u.supporter_tier || 'NONE',
        u.created_at ? new Date(u.created_at).toISOString().split('T')[0] : '',
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scgf-users${optInOnly ? '-optin' : ''}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Roadmap handlers
  const handleSaveRoadmapItem = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/roadmap/${editingItem.id}`, newItem);
      } else {
        await axios.post(`${API}/admin/roadmap`, newItem);
      }
      setNewItem({ title: '', description: '', status: 'PLANNED' });
      setEditingItem(null);
      fetchData();
    } catch (e) {
      alert('Failed to save roadmap item');
    }
  };

  const handleDeleteRoadmapItem = async (id) => {
    if (!window.confirm('Delete this roadmap item?')) return;
    try {
      await axios.delete(`${API}/admin/roadmap/${id}`);
      fetchData();
    } catch (e) {
      alert('Failed to delete item');
    }
  };

  const startEditItem = (item) => {
    setEditingItem(item);
    setNewItem({ title: item.title, description: item.description, status: item.status });
  };

  if (!user?.is_admin) {
    return (
      <div className="navcom-page" data-testid="admin-denied">
        <div className="nc-card" style={{ textAlign: 'center', padding: 60 }}>
          <Shield size={64} style={{ color: 'var(--nc-red)', marginBottom: 24 }} />
          <h2 style={{ fontFamily: 'var(--nc-font-display)', color: 'var(--nc-text-primary)', marginBottom: 16 }}>
            ACCESS DENIED
          </h2>
          <p style={{ color: 'var(--nc-text-muted)' }}>Administrator privileges required</p>
        </div>
      </div>
    );
  }

  return (
    <div className="navcom-page" data-testid="admin-page">
      <div className="page-header">
        <div className="page-label">// ADMIN_PANEL</div>
        <h1 className="page-title">Administration</h1>
      </div>

      {/* Stats Row */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        <div className="nc-stat-card">
          <div className="nc-stat-value">{users.length}</div>
          <div className="nc-stat-label">TOTAL USERS</div>
        </div>
        <div className="nc-stat-card">
          <div className="nc-stat-value">{users.filter(u => u.is_verified).length}</div>
          <div className="nc-stat-label">VERIFIED</div>
        </div>
        <div className="nc-stat-card">
          <div className="nc-stat-value">{users.filter(u => u.supporter_tier && u.supporter_tier !== 'NONE').length}</div>
          <div className="nc-stat-label">SUPPORTERS</div>
        </div>
        <div className="nc-stat-card">
          <div className="nc-stat-value">{users.filter(u => u.is_admin).length}</div>
          <div className="nc-stat-label">ADMINS</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="nc-toggle-group" style={{ marginBottom: 24 }}>
        <button className={`nc-toggle ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
          <Users size={14} /> Users
        </button>
        <button className={`nc-toggle ${activeTab === 'roadmap' ? 'active' : ''}`} onClick={() => setActiveTab('roadmap')}>
          <Map size={14} /> Roadmap
        </button>
      </div>

      {/* Reset Reports Section */}
      <div className="nc-card" style={{ marginBottom: 24, padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h3 style={{ fontFamily: 'var(--nc-font-display)', color: 'var(--nc-text-primary)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Activity size={18} className="text-cyan" /> Signals Data Reset
            </h3>
            <p style={{ fontSize: 13, color: 'var(--nc-text-muted)' }}>
              Last reset: {lastReset ? new Date(lastReset).toLocaleString() : 'Never'}
            </p>
          </div>
          <button className="nc-btn nc-btn-danger" onClick={handleResetReports} disabled={resetting}>
            <RefreshCw size={14} className={resetting ? 'spin' : ''} />
            {resetting ? 'Resetting...' : 'Reset All Reports'}
          </button>
        </div>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
      <div className="nc-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ fontFamily: 'var(--nc-font-display)', color: 'var(--nc-text-primary)', display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
            <Users size={18} className="text-cyan" /> User Management
          </h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => handleExportUsers(false)}
              style={{ background: 'transparent', border: '1px solid var(--nc-cyan)', color: 'var(--nc-cyan)', padding: '6px 14px', fontSize: 11, letterSpacing: 1, fontFamily: 'var(--nc-font-mono)', cursor: 'pointer' }}
              data-testid="export-users-btn"
            >
              📊 EXPORT ALL ({users.length})
            </button>
            <button
              type="button"
              onClick={() => handleExportUsers(true)}
              disabled={users.filter(u => u.contact_opt_in).length === 0}
              style={{ background: 'transparent', border: '1px solid #00ff88', color: '#00ff88', padding: '6px 14px', fontSize: 11, letterSpacing: 1, fontFamily: 'var(--nc-font-mono)', cursor: 'pointer', opacity: users.filter(u => u.contact_opt_in).length === 0 ? 0.4 : 1 }}
              data-testid="export-optin-btn"
            >
              ✉️ OPT-IN ONLY ({users.filter(u => u.contact_opt_in).length})
            </button>
          </div>
        </div>

        {/* Org Approvals panel — always visible so admins can invite new orgs */}
        <div data-testid="org-approvals-panel" style={{
          padding: '12px 16px', marginBottom: 20,
          background: 'rgba(170, 0, 255, 0.05)',
          border: '1px solid rgba(170, 0, 255, 0.3)',
          borderLeft: '3px solid #c977ff',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--nc-font-mono)', fontSize: 11, letterSpacing: 1, color: '#c977ff', padding: '3px 8px', border: '1px solid #c977ff' }}>
              🏢 ORG APPROVALS
            </span>
            <span style={{ fontFamily: 'var(--nc-font-mono)', fontSize: 12, color: 'var(--nc-text-secondary)' }}>
              <strong style={{ color: '#ffd700' }}>{orgs.filter(o => o.approval_status === 'pending').length}</strong> pending ·{' '}
              <strong style={{ color: '#00ff88' }}>{orgs.filter(o => o.approval_status === 'approved').length}</strong> approved ·{' '}
              <strong style={{ color: '#888' }}>{orgs.filter(o => o.approval_status === 'rejected').length}</strong> rejected
            </span>
          </div>

          {/* Admin: invite a user as org owner */}
          <div data-testid="invite-org-owner-form" style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14,
            padding: 12, background: 'rgba(0,0,0,0.2)', border: '1px dashed rgba(170, 0, 255, 0.4)',
          }}>
            <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#c977ff', letterSpacing: 1, fontFamily: 'var(--nc-font-mono)' }}>
              INVITE USER AS ORG OWNER
            </div>
            <input
              placeholder="Org name (e.g. Stanton Rescue Coalition)"
              value={orgInvite.name}
              onChange={e => setOrgInvite(prev => ({ ...prev, name: e.target.value }))}
              className="nc-input"
              style={{ fontSize: 13 }}
              data-testid="invite-org-name"
            />
            <input
              placeholder="Tag (2–8 chars, e.g. SRC)"
              value={orgInvite.tag}
              onChange={e => setOrgInvite(prev => ({ ...prev, tag: e.target.value.toUpperCase() }))}
              className="nc-input"
              style={{ fontSize: 13 }}
              data-testid="invite-org-tag"
            />
            <input
              placeholder="Owner RSI handle (optional if email given)"
              value={orgInvite.owner_handle}
              onChange={e => setOrgInvite(prev => ({ ...prev, owner_handle: e.target.value }))}
              className="nc-input"
              style={{ fontSize: 13 }}
              data-testid="invite-org-handle"
            />
            <input
              placeholder="Owner email (or use handle above)"
              value={orgInvite.owner_email}
              onChange={e => setOrgInvite(prev => ({ ...prev, owner_email: e.target.value }))}
              className="nc-input"
              style={{ fontSize: 13 }}
              data-testid="invite-org-email"
            />
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                type="button"
                onClick={handleInviteOrgOwner}
                disabled={orgInviteBusy || !orgInvite.name.trim() || !orgInvite.tag.trim() || (!orgInvite.owner_handle.trim() && !orgInvite.owner_email.trim())}
                style={{
                  background: 'transparent', border: '1px solid #c977ff', color: '#c977ff',
                  padding: '6px 14px', fontSize: 11, letterSpacing: 1,
                  fontFamily: 'var(--nc-font-mono)', cursor: 'pointer',
                  opacity: orgInviteBusy ? 0.5 : 1,
                }}
                data-testid="invite-org-submit-btn"
              >
                ✉ {orgInviteBusy ? 'CREATING...' : 'CREATE ORG FOR USER'}
              </button>
              {orgInviteMsg && (
                <span data-testid="invite-org-feedback" style={{
                  fontSize: 12, fontFamily: 'var(--nc-font-mono)',
                  color: orgInviteMsg.type === 'ok' ? '#00ff88' : '#ff4466',
                }}>
                  {orgInviteMsg.text}
                </span>
              )}
            </div>
          </div>

          {orgs.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
              <table className="admin-table" style={{ minWidth: 700, fontSize: 12 }}>
                <thead><tr>
                  <th>Tag</th><th>Name</th><th>Specialties</th><th>Discord</th><th>Status</th><th>Actions</th>
                </tr></thead>
                <tbody>
                  {orgs.map(o => (
                    <tr key={o.id} data-testid={`org-row-${o.id}`}>
                      <td style={{ color: 'var(--nc-cyan)', fontFamily: 'var(--nc-font-mono)' }}>{o.tag}</td>
                      <td>{o.name}</td>
                      <td style={{ color: 'var(--nc-text-muted)', fontSize: 11 }}>{(o.specialties || []).join(', ') || '—'}</td>
                      <td>{o.discord_connected ? <Check size={14} className="text-green" /> : <span style={{ color: 'var(--nc-text-muted)' }}>—</span>}</td>
                      <td>
                        {o.approval_status === 'approved' && <span style={{ color: '#00ff88' }}>✅ Approved</span>}
                        {o.approval_status === 'pending' && <span style={{ color: '#ffd700' }}>⏳ Pending</span>}
                        {o.approval_status === 'rejected' && <span style={{ color: '#888' }}>❌ Rejected</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {o.approval_status !== 'approved' && (
                            <button
                              type="button"
                              onClick={() => handleApproveOrg(o.id, true)}
                              style={{ background: 'transparent', border: '1px solid #00ff88', color: '#00ff88', padding: '4px 10px', fontSize: 11, fontFamily: 'var(--nc-font-mono)', cursor: 'pointer' }}
                              data-testid={`approve-org-${o.id}`}
                            >
                              ✅ APPROVE
                            </button>
                          )}
                          {o.approval_status !== 'rejected' && (
                            <button
                              type="button"
                              onClick={() => handleApproveOrg(o.id, false)}
                              style={{ background: 'transparent', border: '1px solid #ff4466', color: '#ff4466', padding: '4px 10px', fontSize: 11, fontFamily: 'var(--nc-font-mono)', cursor: 'pointer' }}
                              data-testid={`reject-org-${o.id}`}
                            >
                              ❌ REJECT
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleReassignOrg(o)}
                            style={{ background: 'transparent', border: '1px solid var(--nc-cyan)', color: 'var(--nc-cyan)', padding: '4px 10px', fontSize: 11, fontFamily: 'var(--nc-font-mono)', cursor: 'pointer' }}
                            data-testid={`reassign-org-${o.id}`}
                            title={`Current owner: ${o.owner_handle || '—'}`}
                          >
                            🔀 REASSIGN
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteOrg(o)}
                            style={{ background: 'transparent', border: '1px solid #ff4466', color: '#ff4466', padding: '4px 10px', fontSize: 11, fontFamily: 'var(--nc-font-mono)', cursor: 'pointer' }}
                            data-testid={`delete-org-${o.id}`}
                          >
                            🗑 DELETE
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: 10, color: 'var(--nc-text-muted)', fontFamily: 'var(--nc-font-mono)', fontSize: 12 }}>
              No organizations yet. Use the form above to invite the first one.
            </div>
          )}
        </div>

        {/* Demo Mode panel */}
        {demoStats && (
          <div data-testid="demo-mode-panel" style={{
            display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
            padding: '12px 16px', marginBottom: 20,
            background: 'rgba(0, 240, 255, 0.05)',
            border: '1px solid rgba(0, 240, 255, 0.3)',
            borderLeft: `3px solid ${demoStats.enabled ? 'var(--nc-cyan)' : '#888'}`,
          }}>
            <span style={{
              fontFamily: 'var(--nc-font-mono)', fontSize: 11, letterSpacing: 1,
              color: demoStats.enabled ? 'var(--nc-cyan)' : '#888',
              padding: '3px 8px', border: `1px solid ${demoStats.enabled ? 'var(--nc-cyan)' : '#888'}`,
            }} data-testid="demo-mode-pill">
              👁 DEMO MODE · {demoStats.enabled ? 'ENABLED' : 'DISABLED'}
            </span>
            <span style={{ fontFamily: 'var(--nc-font-mono)', fontSize: 12, color: 'var(--nc-text-secondary)' }}>
              <strong style={{ color: 'var(--nc-text-primary)' }} data-testid="demo-users-count">{demoStats.demo_users}</strong> demo accounts ·{' '}
              <strong style={{ color: 'var(--nc-text-primary)' }}>{demoStats.demo_contracts}</strong> contracts ·{' '}
              <strong style={{ color: 'var(--nc-text-primary)' }}>{demoStats.demo_bids}</strong> bids
            </span>
            <button
              type="button"
              onClick={handleWipeDemo}
              disabled={wipingDemo || demoStats.demo_users === 0}
              style={{
                marginLeft: 'auto', background: 'transparent',
                border: '1px solid #ff4466', color: '#ff4466',
                padding: '6px 14px', fontSize: 11, letterSpacing: 1,
                fontFamily: 'var(--nc-font-mono)', cursor: 'pointer',
                opacity: (wipingDemo || demoStats.demo_users === 0) ? 0.5 : 1,
              }}
              data-testid="wipe-demo-btn"
            >
              🧹 {wipingDemo ? 'WIPING...' : 'WIPE DEMO DATA'}
            </button>
          </div>
        )}

        {/* Maintenance panel — admin recovery tools */}
        <div data-testid="maintenance-panel" style={{
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
          padding: '12px 16px', marginBottom: 20,
          background: 'rgba(255, 200, 0, 0.05)',
          border: '1px solid rgba(255, 200, 0, 0.3)',
          borderLeft: '3px solid #ffc800',
        }}>
          <span style={{
            fontFamily: 'var(--nc-font-mono)', fontSize: 11, letterSpacing: 1,
            color: '#ffc800',
            padding: '3px 8px', border: '1px solid #ffc800',
          }} data-testid="maintenance-pill">
            🛠 MAINTENANCE
          </span>
          <span style={{ fontFamily: 'var(--nc-font-mono)', fontSize: 12, color: 'var(--nc-text-secondary)' }}>
            One-shot recovery for Future Time posts whose expiry was anchored to creation time instead of scheduled start.
          </span>
          <button
            type="button"
            onClick={handleRepairFuture}
            disabled={repairingFuture}
            style={{
              marginLeft: 'auto', background: 'transparent',
              border: '1px solid #ffc800', color: '#ffc800',
              padding: '6px 14px', fontSize: 11, letterSpacing: 1,
              fontFamily: 'var(--nc-font-mono)', cursor: 'pointer',
              opacity: repairingFuture ? 0.5 : 1,
            }}
            data-testid="repair-future-btn"
          >
            🛠 {repairingFuture ? 'REPAIRING...' : 'REPAIR FUTURE POSTS'}
          </button>
          <button
            type="button"
            onClick={handleRepairMultiJoin}
            disabled={repairingMulti}
            style={{
              background: 'transparent',
              border: '1px solid #ffc800', color: '#ffc800',
              padding: '6px 14px', fontSize: 11, letterSpacing: 1,
              fontFamily: 'var(--nc-font-mono)', cursor: 'pointer',
              opacity: repairingMulti ? 0.5 : 1,
            }}
            data-testid="repair-multi-btn"
          >
            👥 {repairingMulti ? 'REPAIRING...' : 'REPAIR MULTI-JOIN EVENTS'}
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Handle</th>
                <th>Email</th>
                <th>Verified</th>
                <th>Opt-In</th>
                <th>Supporter Tier</th>
                <th>Assign Badge</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={{ color: 'var(--nc-cyan)' }}>{u.rsi_handle}</td>
                  <td>{u.email}</td>
                  <td>{u.is_verified ? <Check size={14} className="text-green" /> : <X size={14} className="text-red" />}</td>
                  <td>{u.contact_opt_in ? <Check size={14} className="text-green" /> : <span style={{ color: 'var(--nc-text-muted)' }}>—</span>}</td>
                  <td>
                    {u.supporter_tier === 'GOLD' && <span style={{ color: '#ffd700' }}>🏆 Gold</span>}
                    {u.supporter_tier === 'SILVER' && <span style={{ color: '#c0c0c0' }}>🛡️ Silver</span>}
                    {u.supporter_tier === 'BRONZE' && <span style={{ color: '#cd7f32' }}>🪙 Bronze</span>}
                    {(!u.supporter_tier || u.supporter_tier === 'NONE') && <span style={{ color: 'var(--nc-text-muted)' }}>—</span>}
                  </td>
                  <td>
                    <select 
                      className="nc-input" 
                      style={{ padding: '4px 8px', fontSize: 12, minWidth: 120 }}
                      onChange={(e) => handleAssignBadge(u.id, e.target.value)}
                      defaultValue=""
                    >
                      <option value="">Assign...</option>
                      <option value="SUPPORTER_BRONZE">🪙 Bronze</option>
                      <option value="SUPPORTER_SILVER">🛡️ Silver</option>
                      <option value="SUPPORTER_GOLD">🏆 Gold</option>
                      <option value="REMOVE_SUPPORTER">❌ Remove</option>
                    </select>
                  </td>
                  <td>{new Date(u.created_at).toLocaleDateString()}</td>
                  <td>
                    {u.is_admin ? (
                      <span style={{ color: 'var(--nc-text-muted)', fontSize: 11 }}>—</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleDeleteUser(u)}
                        style={{ background: 'transparent', border: '1px solid #ff4466', color: '#ff4466', padding: '4px 10px', fontSize: 11, fontFamily: 'var(--nc-font-mono)', cursor: 'pointer', letterSpacing: 1 }}
                        data-testid={`delete-user-${u.id}`}
                      >
                        🗑 DELETE
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Roadmap Tab */}
      {activeTab === 'roadmap' && (
      <div className="nc-card">
        <h3 style={{ fontFamily: 'var(--nc-font-display)', color: 'var(--nc-text-primary)', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Map size={18} className="text-cyan" /> Roadmap Management
        </h3>
        
        {/* Add/Edit Form */}
        <div style={{ background: 'var(--nc-bg-secondary)', padding: 20, marginBottom: 24, border: '1px solid var(--nc-border)' }}>
          <h4 style={{ color: 'var(--nc-cyan)', fontSize: 12, letterSpacing: 1, marginBottom: 16 }}>
            {editingItem ? '// EDIT_ITEM' : '// ADD_NEW_ITEM'}
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              type="text"
              placeholder="Feature Title"
              value={newItem.title}
              onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
              className="nc-input"
              style={{ padding: '12px 16px' }}
              data-testid="roadmap-title-input"
            />
            <textarea
              placeholder="Description"
              value={newItem.description}
              onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              className="nc-input"
              style={{ padding: '12px 16px', minHeight: 80, resize: 'vertical' }}
              data-testid="roadmap-description-input"
            />
            <select
              value={newItem.status}
              onChange={(e) => setNewItem({ ...newItem, status: e.target.value })}
              className="nc-input"
              style={{ padding: '12px 16px' }}
              data-testid="roadmap-status-select"
            >
              {ROADMAP_STATUSES.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 12 }}>
              <button 
                className="nc-btn nc-btn-primary" 
                onClick={handleSaveRoadmapItem}
                disabled={!newItem.title.trim()}
                data-testid="roadmap-save-btn"
              >
                {editingItem ? 'Update' : 'Add'} Item
              </button>
              {editingItem && (
                <button 
                  className="nc-btn nc-btn-secondary"
                  onClick={() => {
                    setEditingItem(null);
                    setNewItem({ title: '', description: '', status: 'PLANNED' });
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Items List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {roadmapItems.length === 0 && (
            <p style={{ color: 'var(--nc-text-muted)', textAlign: 'center', padding: 32 }}>
              No roadmap items yet. Add one above.
            </p>
          )}
          {roadmapItems.map(item => (
            <div key={item.id} style={{ 
              padding: 16, 
              background: 'var(--nc-bg-secondary)', 
              border: '1px solid var(--nc-border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 16
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <span style={{ 
                    fontSize: 11, 
                    padding: '4px 8px', 
                    background: `${STATUS_COLORS[item.status]}20`,
                    border: `1px solid ${STATUS_COLORS[item.status]}`,
                    color: STATUS_COLORS[item.status],
                    letterSpacing: 1
                  }}>
                    {item.status}
                  </span>
                  <h4 style={{ color: 'var(--nc-text-primary)', fontSize: 15, margin: 0 }}>
                    {item.title}
                  </h4>
                </div>
                <p style={{ color: 'var(--nc-text-muted)', fontSize: 13, margin: 0 }}>
                  {item.description}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button 
                  className="nc-btn nc-btn-secondary"
                  onClick={() => startEditItem(item)}
                  style={{ padding: '8px 12px' }}
                  data-testid={`edit-roadmap-${item.id}`}
                >
                  <Edit size={14} />
                </button>
                <button 
                  className="nc-btn nc-btn-danger"
                  onClick={() => handleDeleteRoadmapItem(item.id)}
                  style={{ padding: '8px 12px' }}
                  data-testid={`delete-roadmap-${item.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      <style>{`
        .nc-btn-danger {
          background: transparent;
          border: 1px solid var(--nc-red);
          color: var(--nc-red);
        }
        .nc-btn-danger:hover {
          background: rgba(255, 51, 102, 0.1);
        }
        .admin-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 600px;
        }
        .admin-table th, .admin-table td {
          padding: 12px 16px;
          text-align: left;
          border-bottom: 1px solid var(--nc-border);
        }
        .admin-table th {
          font-family: var(--nc-font-mono);
          font-size: 11px;
          color: var(--nc-text-muted);
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .admin-table td {
          font-size: 13px;
          color: var(--nc-text-secondary);
        }
        .text-green { color: var(--nc-green); }
        .text-red { color: var(--nc-red); }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
